import { useEffect, useState } from 'react';
import { useStandardization } from '../../screens/StandardizationScreen/context';
import useGetModuleScoringTableStructure from './hooks/useGetModuleScoringTableStructure';

import StatusLegend from './StatusLegend';
import CreateEditModuleModal from './CreateEditModuleModal';
import {
  Wrapper,
  RightSideMenuWrapper,
  ContentWrapper,
  MenuIconBtn,
  Row,
  ActionBtnsWrapper,
  ActionBtnsRow,
  StyledActionBtn,
  LayoutListTitle,
  TableWrapper,
  LayoutWrapper,
  Explanation,
  TrainingBar,
  ProjectWrapper,
  LayoutTableTitleWrapper,
} from './styles';
import Loader from '../Loader';
import Table from './Table';
import NotFound from '../../screens/NotFound';
import ProjectInfo from './ProjectInfo';

import useCreateProjectModule from './hooks/useCreateProjectModule';
import useUpdateProjectModule from './hooks/useUpdateProjectModule';
import useDeleteProjectModule from './hooks/useDeleteProjectModule';
import useGetPdfSummaryReport from './hooks/useGetPdfSummaryReport';
import useGetPdfModulesReport from './hooks/useGetPdfModulesReport';
import { toast } from 'react-toastify';
import useCompleteScoringProjectModule from './hooks/useCompleteScoringProjectModule';
import useRejectScoringProjectModule from './hooks/useRejectScoringProjectModule';
import useStandardizationProjectNextPhase from './hooks/useStandardizationProjectNextPhase';
import { parseToFloatIfNumber } from './utils';
import { moduleStatuses } from './enums';
import { useUser } from '../../context/user';
import useUpdateStandardizationProject from './hooks/useUpdateStandardizationProject';
import { parseToFloatOrReturnZero } from './Table/table-config';
import Layout from './Layout';
import { drop, orderBy } from 'lodash';
import { BOX_DOWN, BOX_UP, DROPZONE_DOWN, DROPZONE_UP, DROPZONE_MANIFOLD_UP, DROPZONE_MANIFOLD_DOWN } from './Layout/consts';
import { useIdCatalogSearch } from '../SideMenuSearchLane/context';
import { user_roles } from "../../config/enums";
import ScoringTotals from './ScoringTotals';

export const orderModules = (modules) => orderBy(modules, (m) => m.tsCode, 'asc');

const StandardizationProject = () => {
  const { user } = useUser();
  const phase1EditRoles = [user_roles.stFraming, user_roles.stAdmin, user_roles.admin, user_roles.castAdmin];
  const phase2EditRoles = [user_roles.stProject, user_roles.stAdmin, user_roles.admin, user_roles.castAdmin];
  const nextPhaseRoles = [user_roles.stAdmin, user_roles.admin, user_roles.castAdmin];
  const {
    standardizationProject,
    isStandardizationProjectLoading,
    setSelectedStandardizationProject,
    toggleLeftSideMenu,
      modules,
      setModules,
  } = useStandardization();
  const canEdit = (standardizationProject?.phase === 1 && user?.roles.some(r => phase1EditRoles.includes(r)) && standardizationProject.canEdit) ||
      (standardizationProject?.phase > 1 && user?.roles.some(r => phase2EditRoles.includes(r)) && standardizationProject.canEdit);
  const canNextPhase = user?.roles.some(r => nextPhaseRoles.includes(r));
  const { updateProject, isLoading } = useUpdateStandardizationProject();
  const { nextPhase, isLoading: isLoadingNextPhase } = useStandardizationProjectNextPhase();
  const { getScoringTableStructure } = useGetModuleScoringTableStructure();
  const { createModule, isLoading: isCreatingModule } = useCreateProjectModule();
  const { updateModule, isLoading: isUpdatingModule } = useUpdateProjectModule();
  const { deleteModule, isLoading: isDeletingModule } = useDeleteProjectModule();
  const { completeModuleScoring, isLoading: isCompletingScoring } = useCompleteScoringProjectModule();
  const { rejectModuleScoring, isLoading: isRejectingScoring } = useRejectScoringProjectModule();
  const { getSummaryReport, isLoading: isGetSummaryReportLoading } = useGetPdfSummaryReport();
  const { getModulesReport, isLoading: isGetModulesReportLoading } = useGetPdfModulesReport();
  const [editableProject, setEditableProject] = useState(null);

  const { clearCatalogItems, isSideMenuCollapsed, setIsSideMenuCollapsed, setSideMenuZIndex } = useIdCatalogSearch();
  const nrOfFrames = standardizationProject?.layoutDiagram ? standardizationProject.layoutDiagram.nrOfFrames - 5 : 45; // MPF - 10 as default

  const [moduleModal, setModuleModal] = useState({
    isOpen: false,
    module: null,
    defaultFields: {},
  });
  const [referenceModule, setReferenceModule] = useState(null);

  const onModuleModalClose = () => {
    setModuleModal({
      isOpen: false,
      module: null,
      defaultFields: {},
    });
    setReferenceModule(null);
  };

  const onCatalogCardClick = (card) => {
    if (!moduleModal.isOpen) return;

    setReferenceModule({
      id: card.id,
      project: card.project,
      name: card.name,
      imageId: card.imageId,
      tagId: card.tagId,
      tsCode: card.tsCode,
    });
  };
  // TODO - refactor it so it can work with any columnId i.e column accessor
  const updateModuleCellData = (rowIndex, columnId, value) => {
    const isTotalReasoningColumn = columnId.split('.')[1] === 'totalReasoning';
    const updatedModule = { ...modules[rowIndex] };
    const currentValue = isTotalReasoningColumn
      ? updatedModule.scoringTable.totalReasoning
      : parseToFloatIfNumber(updatedModule[columnId]);
    const newValue = parseToFloatIfNumber(value);
    if (isTotalReasoningColumn) {
      updatedModule.scoringTable = { ...updatedModule.scoringTable, totalReasoning: value };
    } else {
      updatedModule[columnId] = newValue;
    }

    updateModuleLocally(updatedModule);
    if (currentValue !== newValue) {
      updateModule(updatedModule, standardizationProject.id, {
        hideNotifications: true,
        onSuccess: (res) => {
          updateProjectField('siScore', res.totalSiScore);
          updateProjectField('siScoreVendor', res.totalSiScoreVendor);
          updateProjectField('siScoreNoMaturity', res.totalSiScoreNoMaturity);
          updateProjectField('siScoreVendorNoMaturity', res.totalSiScoreVendorNoMaturity);
          updateProjectField('siScoreNonBespoke', res.totalSiScoreNonBespoke);
          updateProjectField('siScoreVendorNonBespoke', res.totalSiScoreVendorNonBespoke);
          updateProjectField('siScoreNonBespokeNoMaturity', res.totalSiScoreNonBespokeNoMaturity);
          updateProjectField('siScoreVendorNonBespokeNoMaturity', res.totalSiScoreVendorNonBespokeNoMaturity);
        },
      });
    }
  };

  const updateModuleLocally = (updatedModule, options = {}) => {
    const { shouldUpdateSiScores = false, shouldResetIsModified = false } = options;
    const _modules = [...modules];
    const ind = _modules.findIndex((m) => m.id === updatedModule.id);
    _modules.splice(ind, 1, { ...updatedModule, isModified: !shouldResetIsModified });
    const changes = { _modules };
    if (shouldUpdateSiScores && updatedModule.totalSiScore && updatedModule.totalSiScoreVendor) {
      changes.siScore = updatedModule.totalSiScore;
      changes.siScoreVendor = updatedModule.totalSiScoreVendor;
    }
    setEditableProject((currentData) => ({ ...currentData, ...changes }));
    setModules(_modules);
  };

  const onAddModule = async (moduleData) => {
    try {
      const scoringTable = (
        await getScoringTableStructure(
          standardizationProject.id,
          moduleData.familyId,
          Boolean(moduleData.secondarySystem)
        )
      ).data;
      const newModule = {
        ...moduleData,
        scoringTable: {
          ...scoringTable,
          intent: moduleData.intent,
        },
      };
      await onSaveModule(newModule);

      onModuleModalClose();
    } catch (err) {
      toast.error(err?.message || 'An error occurred. Try again later.');
    }
  };

  const onSuccessRemoveModule = (moduleId) => {
    const _modules = [...modules];
    const ind = _modules.findIndex((m) => m.id === moduleId);
    _modules.splice(ind, 1);
    setEditableProject((currentData) => ({ ...currentData, _modules }));
    setModules(_modules);
    onModuleModalClose();
    toast('Successfully deleted the module!', {
      type: 'success',
    });
  };

  const onRemoveModule = (module) => {
    deleteModule(module.id, standardizationProject.id, { onSuccess: onSuccessRemoveModule });
  };

  // const replaceModule = (oldModule, newModule) => {
  //   const modules = [...modules];
  //   const ind = modules.findIndex((m) => m.id === oldModule.id);
  //   modules.splice(ind, 1, newModule);
  //   setEditableProject((currentData) => ({ ...currentData, modules }));
  // };

  const addNewModuleLocally = (newModule) => {
    const m = [...modules];
    m.push(newModule);
    const _modules = orderModules(m);
    const changes = { _modules };
    if (newModule.totalSiScore && newModule.totalSiScoreVendor) {
      changes.siScore = newModule.totalSiScore;
      changes.siScoreVendor = newModule.totalSiScoreVendor;
    }
    setEditableProject((currentData) => ({ ...currentData, ...changes }));
    setModules(_modules);
  };

  const onSaveModule = async (module) => {
    if (!module.id) {
      createModule(module, standardizationProject.id, { onSuccess: addNewModuleLocally });
    } else {
      updateModule(module, standardizationProject.id, {
        onSuccess: (updatedModule) =>
          updateModuleLocally(updatedModule, { shouldUpdateSiScores: true, shouldResetIsModified: true }),
      });
    }
  };

  const updateModuleField = (moduleId, field, newData) => {
    const module = { ...modules.find((m) => m.id === moduleId) };
    module[field] = newData;
    updateModuleLocally(module);
  };

  const onScoringCompleted = (module) => {
    completeModuleScoring(module, standardizationProject.id, {
      onSuccess: (moduleId) => updateModuleField(moduleId, 'status', moduleStatuses.COMPLETED.value),
    });
  };

  const onRejectScoring = (module) => {
    rejectModuleScoring(module, standardizationProject.id, {
      onSuccess: (moduleId) => updateModuleField(moduleId, 'status', moduleStatuses.STARTED.value),
    });
  };

  const onNextPhase = () => {
    nextPhase(standardizationProject.id);
  };

  const onGlobalSave = () => {
    const modules = [...modules];
    const modulesToBeSaved = modules.filter((module) => module.isModified);

    const modulesToBeSavedPromises = modulesToBeSaved.map((module) => Promise.resolve(onSaveModule(module)));
    Promise.all(modulesToBeSavedPromises);
    const currentIntegrationWeight = parseToFloatOrReturnZero(standardizationProject.integrationWeight);
    const newIntegrationWeight = parseToFloatOrReturnZero(editableProject.integrationWeight);
    if (currentIntegrationWeight !== newIntegrationWeight) {
      updateProject(editableProject.id, {
        integrationWeight: newIntegrationWeight,
      });
    }
  };

  const onUpdateModuleInCreateEditModal = (module) => {
    // if (module.isInCreation) {
    //   updateModuleLocally(module);
    //   onModuleModalClose();
    // } else {
    module.scoringTable.intent = module.intent;
    updateModule(module, standardizationProject.id, {
      onSuccess: (module) => {
        updateModuleLocally(module);
        onModuleModalClose();
      },
    });
  };

  const updateProjectField = (field, value, persist) => {
    setEditableProject((currentValue) => ({ ...currentValue, [field]: value }));
    if (persist) {
      updateProject(
        editableProject.id,
        {
          [field]: value,
        },
        { hideNotifications: true }
      );
    }
  };

  const onLayoutDropFromOutside = (layoutItemObj, card, dropzoneId) => {
    const defaultFields = {};
    if (dropzoneId === DROPZONE_UP || dropzoneId === BOX_UP || dropzoneId === DROPZONE_MANIFOLD_UP) {
      defaultFields.position = 'Portside';
    } else if (dropzoneId === DROPZONE_DOWN || dropzoneId === BOX_DOWN || dropzoneId === DROPZONE_MANIFOLD_DOWN) {
      defaultFields.position = 'Starboard';
    }

    if (dropzoneId === DROPZONE_UP || dropzoneId === DROPZONE_DOWN || dropzoneId === DROPZONE_MANIFOLD_DOWN || dropzoneId === DROPZONE_MANIFOLD_UP) {
      defaultFields.frameNr = 49 + layoutItemObj.x;
    } else {
      defaultFields.frameNr = 44;
    }

    if (card) {
      defaultFields.referenceModule = card;
      setReferenceModule(card);
    }

    setModuleModal((currentData) => ({
      ...currentData,
      isOpen: true,
      defaultFields,
    }));
  };

  const onLayoutDropFromInside = (module) => {
    const frameNr = module.x;
    const updatedModule = { ...modules.find((m) => m.id === module.id), frameNr };

    updateModuleField(module.id, 'frameNr', frameNr);
    updateModule(updatedModule, standardizationProject.id, { hideNotifications: true });
  };

  useEffect(() => {
    if (!standardizationProject) return;
    const modules = orderModules(standardizationProject.modules);
    setEditableProject({ ...standardizationProject, modules: modules });
    setModules(modules);
  }, [standardizationProject]);

  useEffect(() => {
    if (moduleModal.isOpen) {
      setIsSideMenuCollapsed(true);
    }
  }, [moduleModal.isOpen]);

  const createPdfSummaryReport = () => {
    getSummaryReport(editableProject.id);
  };

  const createPdfModulesReport = () => {
    getModulesReport(editableProject.id);
  };

  return (
    <>
      <Wrapper
        isSideMenuCollapsed={isSideMenuCollapsed}
        isOpened={!isSideMenuCollapsed}>
        {isStandardizationProjectLoading ? (
          <Loader />
        ) : !editableProject ? (
          <NotFound />
        ) : (
          <ProjectWrapper>
            {editableProject.forTraining && <TrainingBar>Training assessment</TrainingBar>}
            <Row alignItems='start' style={{paddingTop:'3rem'}}>
              <ProjectInfo
                stProjectId={editableProject.id}
                updateProject={updateProject}
                name={editableProject.name}
                clientName={editableProject.client}
                phase={editableProject.phase}
                phaseName={editableProject.phaseName}
                isAdmin={canEdit}
                project={editableProject.project}
                projectId={editableProject.projectId}
              />
              <ActionBtnsWrapper>
                <ActionBtnsRow>
                  {canNextPhase && (
                      <>
                        <StyledActionBtn
                            width='205px'
                            title='Next Phase'
                            onClick={onNextPhase}
                        />
                        {/* <StyledActionBtn
                        title='Save'
                        onClick={onGlobalSave}
                      /> */}
                      </>
                  )}
                  <StyledActionBtn
                      title='Close'
                      onClick={() => {
                        setSelectedStandardizationProject(null);
                        toggleLeftSideMenu(true);
                      }}
                  />
                </ActionBtnsRow>
                <ActionBtnsRow>
                  <StyledActionBtn width='120px'
                      title='Summary pdf'
                      onClick={createPdfSummaryReport}
                  />
                </ActionBtnsRow>
              </ActionBtnsWrapper>
            </Row>
            <LayoutWrapper>
              <Layout
                nrOfFrames={nrOfFrames}
                modules={modules}
                onDropFromOutside={onLayoutDropFromOutside}
                onDropFromInside={onLayoutDropFromInside}
                onDropZoneItemClick={(itemId) => {
                  const module = modules.find((m) => m.id === itemId);
                  setModuleModal({
                    isOpen: true,
                    module: module,
                  });
                }}
                onDropZoneClick={() =>
                  setModuleModal((currentValues) => ({
                    ...currentValues,
                    isOpen: true,
                  }))
                }
              />
            </LayoutWrapper>
            {editableProject && modules && modules.length <= 0 && (
              <Explanation>
                Start adding modules. Add modules by clicking on the layout, by clicking the Add module row below, or by
                dragging a reference module from the catalog on the side.
              </Explanation>
            )}
            <LayoutTableTitleWrapper>
              <LayoutListTitle>Layout List</LayoutListTitle>
              <StyledActionBtn width='120px'
                               wrapperStyle={{float:'right',flex:'1',marginRight:'1em'}}
                               title='Modules pdf'
                               onClick={createPdfModulesReport}
              />
            </LayoutTableTitleWrapper>
            <TableWrapper className='table-wrapper'>
              <Table
                modules={modules}
                integrationWeight={editableProject?.integrationWeight}
                projectName={editableProject?.name}
                totalSiScore={editableProject?.siScore}
                totalSiScoreVendor={editableProject?.siScoreVendor}
                updateProjectField={updateProjectField}
                updateModuleCellData={updateModuleCellData}
                updateModule={updateModuleLocally}
                onEditModule={(module) =>
                  setModuleModal({
                    isOpen: true,
                    module: module,
                  })
                }
                onSaveModule={onSaveModule}
                onScoringCompleted={onScoringCompleted}
                onRejectScoring={onRejectScoring}
                isAdmin={canEdit}
                projectPhase={editableProject.phase}
                projectPhaseName={editableProject.phaseName}
              />
            </TableWrapper>
            <ScoringTotals project={editableProject}/>
            <Row className='status-legend-wrapper'>
              <StatusLegend />
              {canEdit && (
                <StyledActionBtn
                  width='200px'
                  title='Add Module Row'
                  onClick={() =>
                    setModuleModal((currentValues) => ({
                      ...currentValues,
                      isOpen: true,
                    }))
                  }
                />
              )}
            </Row>
          </ProjectWrapper>
        )}
      </Wrapper>
      {moduleModal.isOpen && (
        <CreateEditModuleModal
          module={moduleModal.module}
          defaultFields={moduleModal.defaultFields}
          onModalClose={onModuleModalClose}
          isMenuOpen={!isSideMenuCollapsed}
          setIsMenuOpen={(isOpened) => {
            setIsSideMenuCollapsed(!isOpened);
          }}
          referenceModule={referenceModule}
          setReferenceModule={setReferenceModule}
          onAddModule={onAddModule}
          onUpdateModule={onUpdateModuleInCreateEditModal}
          onRemoveModule={onRemoveModule}
          isAdmin={canEdit}
          projectPhase={editableProject.phase}
          onModuleRecommendationClick={onCatalogCardClick}
        />
      )}
    </>
  );
};

export default StandardizationProject;
